import type { DiscontinuedMedicationsResponse } from '@/api/prescriptions/getPrescriptionsWithDiscontinuedMedications';
import type { RescriptPrescriptions } from '@/api/prescriptions/putPrescriptionsBatchReplace';
import { renderHeader } from '@/components/tables/components/Table.utils';
import type { CatalogProduct } from '@/hooks/rest/useGetProductList';
import type { Prescription, ScriptProduct } from '@/types/prescriptions.types';
import dayjs from '@/utils/dayjs';
import { createColumnHelper } from '@tanstack/react-table';
import { BLANK_URL, EMPTY_TABLE_CELL_PLACEHOLDER } from '../../Rescripting.constants';
import {
  buildUniqueRowKey,
  getDefaultAlternativeMedication,
  makeApprovedPrescriptionChangeset,
  makeNewPrescriptionProduct,
  scriptProductToRescriptProduct
} from '../../utils';
import { getPatientRescriptRequestCount } from '../../utils/getPatientRescriptRequestCount';
import { CheckboxCell } from '../shared/tableCells/CheckboxCell';
import { MedicationsOnScriptCell } from '../shared/tableCells/MedicationsOnScriptCell/MedicationsOnScriptCell';
import { PatientCodeLinkCell } from '../shared/tableCells/PatientCodeLinkCell/PatientCodeLinkCell';
import { PatientRescriptRequestCountHeaderCell } from '../shared/tableCells/PatientRescriptRequestCountHeaderCell/PatientRescriptRequestCountHeaderCell';
import { PriorityChip } from '../shared/tableCells/PriorityChip/PriorityChip';
import { ProductSelectCell } from '../shared/tableCells/ProductSelectCell';
import type { DiscontinuedTableRowData } from './DiscontinuedPanel.types';
import { DiscontinuedTableColumnHeaders } from './DiscontinuedPanel.types';

const isDiscontinuedMedication = (product: ScriptProduct): boolean => product.is_generative_scripting;

const buildDiscontinuedMedicationTableHeaders = (
  alternativeProductList: CatalogProduct[],
  onAlternativeMedicationSelect: React.Dispatch<React.SetStateAction<Map<string, CatalogProduct>>>
) => {
  const columnHelper = createColumnHelper<DiscontinuedTableRowData>();

  return [
    columnHelper.display({
      id: 'checkBoxCell',
      cell: ({ row }) => {
        const isRowSelected = row.getIsSelected();
        const selectedAlternativeProductId = row.original.selectedAlternativeProductId;
        return (
          <CheckboxCell
            isRowSelected={isRowSelected}
            selectedAlternativeProductId={selectedAlternativeProductId}
            alternativeProductList={alternativeProductList}
          />
        );
      },
      meta: {
        sx: {
          width: 25
        }
      }
    }),
    columnHelper.accessor('ageOfRescriptRequest', {
      id: 'ageOfRescriptRequest',
      header: ({ table }) => {
        const patientRescriptRequestCount = getPatientRescriptRequestCount(table.getCoreRowModel());

        return (
          <PatientRescriptRequestCountHeaderCell
            headerLabel={DiscontinuedTableColumnHeaders.Age_Of_Rescript_Request}
            count={patientRescriptRequestCount}
          />
        );
      },
      cell: (cell) => <PriorityChip value={cell.getValue()} />,
      meta: {
        sx: {
          width: 100
        }
      }
    }),
    columnHelper.accessor('patientName', {
      header: () => renderHeader(DiscontinuedTableColumnHeaders.Patient_Name),
      cell: (cell) => {
        return cell.getValue() ? cell.getValue() : '-';
      },
      meta: {
        sx: {
          width: 120
        }
      }
    }),
    columnHelper.accessor('patientCode', {
      header: () => renderHeader(DiscontinuedTableColumnHeaders.Patient_Code),
      cell: ({ cell, row }) => {
        const patientCode = cell.getValue();
        const patientId = row.original.patientId;
        return <PatientCodeLinkCell patientCode={patientCode} patientId={patientId} />;
      },
      meta: {
        sx: {
          width: 80
        }
      }
    }),
    columnHelper.accessor('discontinuedMedicationName', {
      header: () => renderHeader(DiscontinuedTableColumnHeaders.Discontinued_Medication),
      cell: (cell) => {
        return cell.getValue() ? cell.getValue() : '-';
      },
      meta: {
        sx: {
          width: 160
        }
      }
    }),
    columnHelper.accessor('allMedicationsOnScript', {
      header: () => renderHeader(DiscontinuedTableColumnHeaders.Script_Medications),
      cell: ({ cell, row }) => {
        const rowId = row.id;
        const medications = cell.getValue().map((medication) => ({
          name: medication.name,
          toFlag: medication.isDiscontinued
        }));

        return <MedicationsOnScriptCell medications={medications} rowId={rowId} />;
      },
      meta: {
        sx: {
          width: 160
        }
      }
    }),
    columnHelper.accessor('selectedAlternativeProductId', {
      header: () => renderHeader(DiscontinuedTableColumnHeaders.Alternative_Medication_List),
      cell: ({ cell, row }) => (
        <ProductSelectCell
          value={cell.getValue() ? Number(cell.getValue()) : ''}
          options={alternativeProductList ?? []}
          onSelect={(selectedProduct) => {
            if (selectedProduct) {
              onAlternativeMedicationSelect((prevMap) =>
                new Map(prevMap).set(row.original.uniqueRowKey, selectedProduct)
              );
            }
          }}
        />
      ),
      meta: {
        sx: {
          width: 160
        }
      }
    }),
    columnHelper.accessor('doctorName', {
      header: () => renderHeader(DiscontinuedTableColumnHeaders.Doctor_Name),
      cell: (cell) => {
        return cell.getValue() ? cell.getValue() : '-';
      },
      meta: {
        sx: {
          width: 160
        }
      }
    })
  ];
};

/**
 * Builds out an array of objects that represent the data for each table
 * row. This is done by first looping over the prescriptions and storing
 * an array that houses the discontinued medications that are apart of
 * that prescription. That array is then mapped over to construct the
 * specific data required within a table row.
 *
 * @param discontinuedMedicationsResponse an object that contains a field
 * for the prescriptions that include one or more discontinued medications
 * @param modifiedScripts a map that contains a key value pairing of the
 * uniqueRowKey to the id of the alternative medication selected
 * @returns an array of table row data to be consumed by a table component
 */
const buildDiscontinuedMedicationTableData = (
  discontinuedMedicationsResponse: DiscontinuedMedicationsResponse | undefined,
  modifiedScripts: Map<string, CatalogProduct>
): DiscontinuedTableRowData[] => {
  const discontinuedTableRowData: DiscontinuedTableRowData[] = [];

  discontinuedMedicationsResponse?.prescriptions.forEach((prescription: Prescription) => {
    const discontinuedMedications = prescription.Products.filter((product) => isDiscontinuedMedication(product));

    const discontinuedMedicationTableRows = discontinuedMedications.map((product) => {
      const { id } = prescription;

      const {
        PatientUser = {
          first_name: EMPTY_TABLE_CELL_PLACEHOLDER,
          last_name: EMPTY_TABLE_CELL_PLACEHOLDER
        },
        patient_code = null,
        id: patientId = null
      } = prescription.Patient ?? {};
      const { first_name, last_name } = prescription.Doctor.DoctorUser;
      const uniqueRowKey = buildUniqueRowKey(id, product.id);
      const modifiedAlternative = modifiedScripts.get(uniqueRowKey);
      const defaultAlternative = product?.alternativeProducts[0];
      const selectedAlternativeId =
        (modifiedAlternative && modifiedAlternative.id) || (defaultAlternative && defaultAlternative.id) || 0;
      const ageOfRescriptRequest = dayjs(product.patient_rescript_request_date).isValid()
        ? dayjs(product.patient_rescript_request_date).fromNow()
        : '';

      return {
        uniqueRowKey,
        patientName: `${PatientUser.first_name} ${PatientUser.last_name}`,
        patientCode: patient_code || EMPTY_TABLE_CELL_PLACEHOLDER,
        patientId: patientId?.toString() || BLANK_URL,
        discontinuedMedicationName: product.name,
        allMedicationsOnScript: prescription.Products.map((product) => ({
          name: product.name,
          isDiscontinued: isDiscontinuedMedication(product)
        })),
        selectedAlternativeProductId: selectedAlternativeId,
        doctorName: `${first_name || ''} ${last_name || ''}`,
        ageOfRescriptRequest
      };
    });

    discontinuedTableRowData.push(...discontinuedMedicationTableRows);
  });

  return discontinuedTableRowData;
};

/**
 * Loops over the approved script changesets and the products within
 * them to construct an array of prescriptions to rescript
 *
 * @param selectedUniqueRowKeys an array of selected unique row keys
 * @param prescriptionsWithOOSMedications an array of prescriptions that
 * have out of stock medications
 * @param modifiedScripts a map that holds prescriptionId to medication
 * data
 * @returns an array of rescript prescriptions
 */
const buildDiscontinuedRescriptPutBody = (
  selectedUniqueRowKeys: string[],
  prescriptionsWithDiscontinuedMedications: Prescription[],
  modifiedScripts: Map<string, CatalogProduct>
) => {
  const originalScriptPerId = new Map(prescriptionsWithDiscontinuedMedications.map((script) => [script.id, script]));
  const approvedScriptChangeset = makeApprovedPrescriptionChangeset(selectedUniqueRowKeys, modifiedScripts);
  const rescriptPrescriptions: RescriptPrescriptions[] = [];

  approvedScriptChangeset.forEach((approvedProductChangeset, scriptId) => {
    const originalScript = originalScriptPerId.get(scriptId);
    const originalScriptProducts = originalScript?.Products || [];
    const newScriptProducts = originalScriptProducts.map(scriptProductToRescriptProduct);

    approvedProductChangeset.forEach((modifiedScriptProduct, productId) => {
      const currentIndex = newScriptProducts.findIndex((rescriptProduct) => rescriptProduct.productId === productId);
      const currentRescriptProduct = newScriptProducts[currentIndex];
      const currentScriptProduct = originalScriptProducts.find((scriptProduct) => scriptProduct.id === productId);
      const defaultAlternativeProduct = getDefaultAlternativeMedication(currentScriptProduct);
      newScriptProducts[currentIndex] = makeNewPrescriptionProduct(
        currentRescriptProduct,
        modifiedScriptProduct,
        defaultAlternativeProduct
      );
    });
    rescriptPrescriptions.push({
      originalPrescriptionId: scriptId,
      newPrescription: {
        orderDate: new Date().toISOString(),
        patientId: originalScript?.Patient?.id,
        // Use logged in doctor's id.
        doctorId: originalScript?.Doctor.id,
        products: newScriptProducts
      }
    });
  });

  return rescriptPrescriptions;
};

export {
  buildDiscontinuedMedicationTableData,
  buildDiscontinuedMedicationTableHeaders,
  buildDiscontinuedRescriptPutBody
};
